import React from "react"
import { Col, Row, Table } from "react-bootstrap"
import { useField } from "formik"
import _ from "lodash"

import FormikBootstrap from "@/components/formik-bootstrap"
import TextDisplay from "@/onboarding/features/product/components/shared/TextDisplay"
import Pricing from "@/pricing"
import Routes from "@/routes"
import RateInputs from "@/onboarding/features/product/components/shared/RateInputs"
import ComponentOperatorSelect from "@/onboarding/features/product/components/shared/ComponentOperatorSelect"

import OperatorDisplay from "./OperatorDisplay"
import TenderStatus from "./TenderStatus"

const FileDownloadMessage = (): JSX.Element => {
  const [{ value: menuSrc }] = useField("menuSrc")

  const newFileIsSelected = menuSrc?.value instanceof File
  const savedFileIsPresent = !newFileIsSelected && !_.isEmpty(menuSrc)

  if (savedFileIsPresent) {
    return (
      <a href={Routes.file_holder_path({ id: menuSrc })} download className="text-underline">
        Download current Sample Menu
      </a>
    )
  }

  if (newFileIsSelected) {
    return <>Submit to save Sample Menu</>
  }

  return <>No Sample Menu saved</>
}

const SampleDownload = ({ menuSourceFileId }): JSX.Element => {
  if (_.isEmpty(menuSourceFileId)) return <>N/A</>

  const menuSrc = typeof menuSourceFileId === "string" ? menuSourceFileId : menuSourceFileId.value
  if (!_.isEmpty(menuSrc)) {
    return (
      <a href={Routes.file_holder_path({ id: menuSrc })} download className="text-underline">
        Download
      </a>
    )
  }

  return <>N/A</>
}

const WriteView = ({ hideRates = false, showOperator = false, tender = null }): JSX.Element => (
  <div className="d-flex flex-column gap-2">
    <Row>
      <Col md={8} className="d-flex flex-column gap-2">
        <h5 className="mb-0">
          Edit Details
          <TenderStatus tender={tender} />
        </h5>
        <Row>
          <Col md={3}>
            {/*
              The name field is required.
              There is an open feature request for extracting requiredness from the schema: https://github.com/jaredpalmer/formik/issues/1241
            */}
            <FormikBootstrap.TextInput name="name" label="Name *" />
          </Col>
          <Col md={3}>
            <FormikBootstrap.NumberInput name="capacity" label="Capacity" />
          </Col>
          <Col md={3}>
            <FormikBootstrap.FileInput name="menuSrc" label="Sample Menu" />
            <FileDownloadMessage />
          </Col>
        </Row>
        <Row>
          <Col md={9}>
            <FormikBootstrap.TextInput name="description" label="Menu Description" />
          </Col>
        </Row>
        {showOperator && (
          <Row>
            <Col md={9}>
              <ComponentOperatorSelect />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={4}>
            <FormikBootstrap.Checkbox name="restroomsAvailable" label="Restrooms Available?" />
          </Col>
          <Col md={4}>
            <FormikBootstrap.Checkbox name="liftsAvailable" label="Wheelchair Lifts Available?" />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <FormikBootstrap.Checkbox name="airConditioned" label="Air Conditioned?" />
          </Col>
          <Col md={4}>
            <FormikBootstrap.Checkbox name="rampsAvailable" label="Wheelchair Ramps Available?" />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <FormikBootstrap.Checkbox name="buffetService" label="Buffet Service?" />
          </Col>
          <Col md={4}>
            <FormikBootstrap.Checkbox name="wheelchairAccessible" label="Wheelchair Accessible?" />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <FormikBootstrap.Checkbox name="waiterService" label="Waiter Service?" />
          </Col>
          <Col md={4}>
            <FormikBootstrap.Checkbox
              name="specialDietaryMeals"
              label="Special Dietary Meals Offered By Request?"
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <FormikBootstrap.Checkbox name="vegetarianMeals" label="Vegetarian Meals Offered?" />
          </Col>
        </Row>
      </Col>
      {!hideRates && (
        <Col md={4} className="ms-n4">
          <h5 className="mb-0">Edit Rates</h5>
          <RateInputs name="ratesheet" />
        </Col>
      )}
    </Row>
  </div>
)

const ReadView = ({
  component,
  hideRates,
  showOperator,
  tender,
  previousValues = false,
}): JSX.Element => {
  const textStyle = previousValues ? "original-product-text" : ""

  return (
    <div className="d-flex flex-column gap-2">
      <h5 className={textStyle}>
        Details
        <TenderStatus tender={tender} />
      </h5>
      <Table>
        <thead>
          <tr>
            <th className={textStyle}>Name</th>
            <th className={textStyle}>Description</th>
            <th className={textStyle}>Capacity</th>
            <th className={textStyle}>Sample Menu</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td aria-label="Name">
              <TextDisplay value={component.name} previousValue={previousValues} />
            </td>
            <td aria-label="Description">
              <TextDisplay value={component.description} previousValue={previousValues} />
            </td>
            <td aria-label="Capacity">
              <TextDisplay value={component.capacity} previousValue={previousValues} />
            </td>
            <td aria-label="Sample Menu">
              <SampleDownload menuSourceFileId={component.menuSrc} />
            </td>
          </tr>
        </tbody>
        <thead>
          <tr className="text-wrap">
            <th className={textStyle}>Air Conditioned?</th>
            <th className={textStyle}>Waiter Service?</th>
            <th className={textStyle}>Buffet Service?</th>
            <th className={textStyle}>Special Dietary Meals Offered By Request?</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td aria-label="Air Conditioned?">
              <TextDisplay value={component.airConditioned} previousValue={previousValues} />
            </td>
            <td aria-label="Waiter Service?">
              <TextDisplay value={component.waiterService} previousValue={previousValues} />
            </td>
            <td aria-label="Buffet Service?">
              <TextDisplay value={component.buffetService} previousValue={previousValues} />
            </td>
            <td aria-label="Special Dietary Meals Offered By Request?">
              <TextDisplay value={component.specialDietaryMeals} previousValue={previousValues} />
            </td>
          </tr>
        </tbody>
        <thead>
          <tr className="text-wrap">
            <th className={textStyle}>Vegetarian Meals Offered?</th>
            <th className={textStyle}>Wheelchair Accessible?</th>
            <th className={textStyle}>Wheelchair Ramps Available?</th>
            <th className={textStyle}>Wheelchair Lifts Available?</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td aria-label="Vegetarian Meals Offered?">
              <TextDisplay value={component.vegetarianMeals} previousValue={previousValues} />
            </td>
            <td aria-label="Wheelchair Accessible?">
              <TextDisplay value={component.wheelchairAccessible} previousValue={previousValues} />
            </td>
            <td aria-label="Wheelchair Ramps Available?">
              <TextDisplay value={component.rampsAvailable} previousValue={previousValues} />
            </td>
            <td aria-label="Wheelchair Lifts Available?">
              <TextDisplay value={component.liftsAvailable} previousValue={previousValues} />
            </td>
          </tr>
        </tbody>
        <thead>
          <tr className="text-wrap">
            <th colSpan={showOperator ? 1 : 4} className={textStyle}>
              Restrooms Available?
            </th>
            {showOperator && (
              <th colSpan={3} className={textStyle}>
                Operator
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={showOperator ? 1 : 4} aria-label="Restrooms Available?">
              <TextDisplay value={component.restroomsAvailable} previousValue={previousValues} />
            </td>
            {showOperator && (
              <td colSpan={3} aria-label="Operator">
                <OperatorDisplay operatorId={component.operatorId} previousValue={previousValues} />
              </td>
            )}
          </tr>
        </tbody>
      </Table>
      {!hideRates && (
        <Col md={3}>
          <h5 className={`mt-2 ${textStyle}`}>Rates</h5>
          <Pricing.View value={component.ratesheet} previousValues={previousValues} />
        </Col>
      )}
    </div>
  )
}

const View = ({
  component,
  previousValues = false,
  editing = false,
  hideRates = false,
  showOperator = false,
  tender = null,
}): JSX.Element =>
  editing ? (
    <WriteView hideRates={hideRates} showOperator={showOperator} tender={tender} />
  ) : (
    <ReadView
      component={component}
      hideRates={hideRates}
      showOperator={showOperator}
      previousValues={previousValues}
      tender={tender}
    />
  )

export default View
